import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Globe, RefreshCw } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { http } from "@/lib/api";
import { keys, usePublications } from "@/lib/queries";
import { formatDate } from "@/lib/utils";
import { ConfirmDialog, CopyButton, toastError } from "./common";
import { Button, Dialog, Field, Input, Select, Spinner, Switch } from "./ui";

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fromLocalInput = (v: string) => (v ? new Date(`${v}T23:59:59`).toISOString() : null);

/**
 * Warning shown when iOS builds of the app cannot be installed from public pages: the App Store
 * Connect key of the app has no distribution certificate. Publishing stays possible.
 */
export function AdhocSigningWarning({ app, build }: { app: S.App; build?: S.Build }) {
  const { t } = useTranslation();
  const concernsIos = build ? build.platform === "ios" : !!app.iosBundleId;
  if (!concernsIos || app.adhocSigning) return null;
  return (
    <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{t(app.appleCredentialId ? "publish.noCertificate" : "publish.noAppleKey")}</span>
    </div>
  );
}

/** App page only: list new builds on the page as soon as they are uploaded. */
function AutoPublishToggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2">
      <span className="flex-1">
        <span className="block text-sm font-medium">{t("publish.autoPublish")}</span>
        <span className="block text-xs text-fg-muted">{t("publish.autoPublishHelp")}</span>
      </span>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </label>
  );
}

/** Publish / manage the public page of an app (no build) or of one build. */
export function PublishDialog({ app, build, open, onOpenChange }: { app: S.App; build?: S.Build; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const appId = app.id;
  const qc = useQueryClient();
  const publications = usePublications(appId, open);
  const current = publications.data?.find((p) => p.buildId === (build?.id ?? null)) ?? null;
  const [approval, setApproval] = useState<S.DeviceApproval>("admin");
  const [expires, setExpires] = useState("");
  const [autoPublish, setAutoPublish] = useState(false);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.publications(appId) });

  const run = async (fn: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      if (success) toast.success(success);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  const update = (body: Record<string, unknown>) => run(() => http.patch(`/publications/${current!.id}`, body), t("common.saved"));

  const showsIos = !build || build.platform === "ios";
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={build ? t("publish.buildTitle", { version: `${build.version} (${build.buildNumber})` }) : t("publish.appTitle")}
      description={build ? t("publish.buildHelp") : t("publish.appHelp")}
      wide
    >
      <div className="mb-4 empty:hidden">
        <AdhocSigningWarning app={app} build={build} />
      </div>
      {publications.isLoading ? (
        <Spinner />
      ) : !current ? (
        <div className="space-y-4">
          {showsIos && (
            <Field label={t("publish.approval")} help={t("publish.approvalHelp")} htmlFor="pub-approval">
              <Select id="pub-approval" value={approval} onChange={(e) => setApproval(e.target.value as S.DeviceApproval)}>
                <option value="admin">{t("publish.approval_admin")}</option>
                <option value="auto">{t("publish.approval_auto")}</option>
              </Select>
            </Field>
          )}
          {!build && <AutoPublishToggle checked={autoPublish} onChange={setAutoPublish} />}
          <Field label={t("publish.expires")} help={t("publish.expiresHelp")} htmlFor="pub-expires">
            <Input id="pub-expires" type="date" value={expires} min={toLocalInput(new Date().toISOString())} onChange={(e) => setExpires(e.target.value)} className="w-48" />
          </Field>
          <Button
            loading={busy}
            onClick={() =>
              run(() => http.post(`/apps/${appId}/publications`, { buildId: build?.id ?? null, deviceApproval: approval, autoPublishBuilds: !build && autoPublish, expiresAt: fromLocalInput(expires) }), t("publish.published"))
            }
          >
            <Globe className="h-4 w-4" />
            {t("publish.publish")}
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("publish.link")}</p>
            <div className="flex flex-wrap items-center gap-2">
              <Input readOnly value={current.url} className="min-w-0 flex-1 font-mono text-xs" onFocus={(e) => e.target.select()} />
              <CopyButton value={current.url} />
              <a href={current.url} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t("publish.open")}
                </Button>
              </a>
            </div>
            <p className="text-xs text-fg-muted">
              {current.expiresAt ? t("publish.expiresOn", { date: formatDate(current.expiresAt, i18n.language) }) : t("publish.noExpiry")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {showsIos && (
              <Field label={t("publish.approval")} help={t("publish.approvalHelp")} htmlFor="pub-approval-edit">
                <Select id="pub-approval-edit" value={current.deviceApproval} disabled={busy} onChange={(e) => update({ deviceApproval: e.target.value })}>
                  <option value="admin">{t("publish.approval_admin")}</option>
                  <option value="auto">{t("publish.approval_auto")}</option>
                </Select>
              </Field>
            )}
            <Field label={t("publish.expires")} htmlFor="pub-expires-edit">
              <Input
                id="pub-expires-edit"
                type="date"
                defaultValue={toLocalInput(current.expiresAt)}
                min={toLocalInput(new Date().toISOString())}
                disabled={busy}
                onBlur={(e) => {
                  if (e.target.value !== toLocalInput(current.expiresAt)) void update({ expiresAt: fromLocalInput(e.target.value) });
                }}
                className="w-48"
              />
            </Field>
          </div>
          {!build && (
            <div className="space-y-2">
              <AutoPublishToggle checked={current.autoPublishBuilds} onChange={(v) => update({ autoPublishBuilds: v })} disabled={busy} />
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-fg-muted">{t(current.autoPublishBuilds ? "publish.pickBuildsAuto" : "publish.pickBuilds")}</p>
            </div>
          )}
          <div className="flex flex-wrap justify-between gap-2 border-t pt-4">
            <ConfirmDialog
              title={t("publish.regenerate")}
              description={t("publish.regenerateHelp")}
              onConfirm={() => update({ regenerateToken: true })}
              trigger={(openConfirm) => (
                <Button variant="outline" size="sm" onClick={openConfirm} disabled={busy}>
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t("publish.regenerate")}
                </Button>
              )}
            />
            <ConfirmDialog
              danger
              title={t("publish.unpublish")}
              description={t("publish.unpublishHelp")}
              confirmLabel={t("publish.unpublish")}
              onConfirm={() => run(() => http.del(`/publications/${current.id}`), t("publish.unpublished"))}
              trigger={(openConfirm) => (
                <Button variant="danger" size="sm" onClick={openConfirm} disabled={busy}>
                  {t("publish.unpublish")}
                </Button>
              )}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}

export function PublishButton({ app, build }: { app: S.App; build?: S.Build }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Globe className="h-4 w-4" />
        {t("publish.button")}
      </Button>
      <PublishDialog app={app} build={build} open={open} onOpenChange={setOpen} />
    </>
  );
}
